import {
    Checkbox,
    Collapse,
    List,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Box } from '@mui/system';
import { useState } from 'react';
import { ProjectSelectableResource } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import {
    getMediaBasePath,
    IconWrapper,
    ImageWrapper,
    IStyles,
    pxToRem,
    pxTovW,
    resourceTypeName,
    theme,
} from '@geneo2-web/shared-ui';

const styles: IStyles = {
    card: {
        cursor: 'pointer',
        width: '100%',
        padding: 0,
        boxSizing: 'border-box',

        borderRadius: { xs: pxToRem(15), md: pxToRem(15), lg: pxTovW(15) },
        justifyContent: 'center',
        display: 'flex',
    },

    iconTextBoxContainer: {
        width: '100%',
        display: 'flex',
        gap: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
        alignItems: 'center',
    },
};

interface IProps {
    title?: string;
    subtitle?: string;
    posterImageUrl?: string;
    totalResourceCount: number;
    selectedCount: number;
    variant: 'small' | 'medium' | 'large';
    /** Omit to hide the resource picker - used on the "added" summary. */
    resources?: ProjectSelectableResource[];
    isResourceChecked?: (resource: ProjectSelectableResource) => boolean;
    isResourceDisabled?: (resource: ProjectSelectableResource) => boolean;
    toggleHandler?: () => void;
    resourceToggleHandler?: (resource: ProjectSelectableResource) => void;
    deleteHandler?: () => void;
}

export const LessonPlanSelectionCard = (props: IProps) => {
    const {
        title,
        subtitle,
        posterImageUrl,
        totalResourceCount,
        selectedCount,
        variant,
        resources,
        isResourceChecked,
        isResourceDisabled,
        toggleHandler,
        resourceToggleHandler,
        deleteHandler,
    } = props;
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
    const isIpadOnly = !isMobile && isIpad;
    const [isOpen, setIsOpen] = useState(false);

    const selected = selectedCount > 0;
    const hasResources = totalResourceCount > 0;
    const showResourcePicker = !!resources;

    return (
        <List
            sx={{
                ...styles.card,
                backgroundColor: selected ? '#F2F9FF' : '#FFFFFF',
                border: selected ? '1.5px solid #007CDC' : 'none',
                boxShadow: selected ? 'none' : '0px 0px 6px #00000029',
                flexDirection: 'column',
                position: 'relative',
                zIndex: 0,
                opacity: 1,
                cursor: hasResources ? 'pointer' : 'default',
            }}
            onClick={() => {
                if (!hasResources) return;
                setIsOpen(false);
                toggleHandler?.();
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    position: 'relative',
                    padding: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: pxToRem(10),
                    }}
                >
                    <ImageWrapper
                        name={'chapterImage'}
                        type="png"
                        parentFolder={'tempAssets'}
                        path={getMediaBasePath(
                            posterImageUrl,
                            'processedMediaBucket'
                        )}
                        styles={{
                            height: { xs: pxToRem(68), md: pxToRem(79), lg: pxTovW(121) },
                            width: { xs: pxToRem(68), md: pxToRem(79), lg: pxTovW(121) },
                            objectFit: 'cover',
                            borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
                        }}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                height: '100%',
                                gap: pxToRem(7.5),
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="h3"
                                    fontWeight={700}
                                    fontFamily={'lato'}
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        width: {
                                            xs: pxToRem(150),
                                            md: pxTovW(280),
                                            lg: pxTovW(230),
                                        },
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {title}
                                </Typography>
                                {subtitle && (
                                    <Typography
                                        variant="subText"
                                        color="secondary.main"
                                    >
                                        {subtitle}
                                    </Typography>
                                )}
                            </Box>

                            {hasResources ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: pxToRem(5),
                                    }}
                                >
                                    <Typography
                                        variant={isIpadOnly ? 'h4' : 'h5'}
                                        color="#007CDC"
                                    >
                                        {selected
                                            ? `${selectedCount}/${totalResourceCount} resources`
                                            : `${totalResourceCount} resources`}
                                    </Typography>
                                    {showResourcePicker && (
                                        <Box
                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                event?.stopPropagation();
                                                setIsOpen(!isOpen);
                                            }}
                                        >
                                            <IconWrapper
                                                name="edit"
                                                size="md"
                                                type="png"
                                                parentFolder="icons"
                                                customSx={{
                                                    border: '1px solid #0AA34F',
                                                    borderRadius: { xs: pxToRem(3), md: pxToRem(5), lg: pxTovW(5) },
                                                    width: {
                                                        xs: pxToRem(14),
                                                        md: pxTovW(35),
                                                        lg: pxTovW(23),
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Typography
                                    variant={isIpadOnly ? 'h4' : 'h5'}
                                    color="text.disabled"
                                >
                                    No resources yet
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {showResourcePicker && (
                <Collapse
                    in={isOpen}
                    timeout="auto"
                    unmountOnExit
                    sx={{ width: '100%', mt: { xs: pxToRem(5), lg: pxTovW(5) } }}
                    // Picking inside the tree must not toggle the whole chapter.
                    onClick={(event) => event.stopPropagation()}
                >
                    <List
                        component="div"
                        disablePadding
                        sx={{
                            height: 'max-content',
                            maxHeight: { xs: 'max-content', md: pxTovW(450), lg: pxTovW(290) },
                            overflowY: 'auto',
                            scrollbarWidth: 'thin',
                        }}
                    >
                        {resources.map((resource) => (
                            <Box
                                key={resource.resourceId}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    minHeight: variantMapping[variant]?.height,
                                    padding: '0px 10px',
                                    boxSizing: 'border-box',
                                    alignItems: 'center',
                                    borderTop: '1px solid #B5D9F6',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        gap: { xs: pxToRem(10), lg: pxTovW(10) },
                                        alignItems: 'center',
                                        paddingY: { xs: pxToRem(10), lg: pxTovW(10) },
                                    }}
                                >
                                    <Checkbox
                                        sx={{ height: '24px', width: '24px' }}
                                        value={resource.resourceId}
                                        checked={!!isResourceChecked?.(resource)}
                                        disabled={!!isResourceDisabled?.(resource)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => resourceToggleHandler?.(resource)}
                                    />
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            flexGrow: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {resource.title}
                                    </Typography>
                                    <Typography
                                        variant="subText"
                                        color="secondary.main"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        {resourceTypeName(resource.resourceType).name}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </List>
                </Collapse>
            )}

            {hasResources && (
                <IconWrapper
                    name={selected ? 'deletehw' : 'addHw'}
                    size="md"
                    type="png"
                    parentFolder="icons"
                    // Without a deleteHandler the click bubbles to the card's toggle.
                    onClick={() => {
                        if (selected && deleteHandler) {
                            setIsOpen(false);
                            deleteHandler();
                        }
                    }}
                    customSx={{
                        width: { xs: pxToRem(17), md: pxTovW(35), lg: pxTovW(24) },
                        margin: { xs: pxToRem(10), md: 0 },
                        position: 'absolute',
                        zIndex: 1,
                        right: { xs: pxToRem(10), lg: pxTovW(10) },
                        top: { xs: pxToRem(10), lg: pxTovW(10) },
                    }}
                />
            )}
        </List>
    );
};
const variantMapping = {
    small: {
        height: { xs: pxToRem(25), md: pxToRem(24), lg: pxTovW(30) },
    },
    medium: {
        height: { xs: pxToRem(35), md: pxToRem(27), lg: pxTovW(35) },
    },

    large: {
        height: { xs: pxToRem(54), md: pxToRem(60), lg: pxTovW(90) },
    },
};
